import HttpClient from "@lib/http-client";

// Session routes are scoped to the target org — the org that owns the
// classroom the assistant was launched from — validated server-side.
const sessionsUrl = (orgCode, path = "") =>
  `/ai-curriculum/organizations/${orgCode}/sessions${path}`;

export default class AiCurriculumService extends HttpClient {
  // Caller-org semantics: is AICA enabled for the user's own org.
  getFeatureEnabled() {
    return this.http.get("/ai-curriculum/feature-enabled");
  }

  createSession(orgCode, entryType) {
    return this.http.post(sessionsUrl(orgCode), { entryType });
  }

  getSessionStatus(orgCode, sessionId) {
    return this.http.get(sessionsUrl(orgCode, `/${sessionId}/status`));
  }

  // Full session state for rehydrating the wizard after a refresh
  // (ENG-23488): status/failedStage plus entryType, standardsContext and the
  // submitted criteria. Kept off /status, which polls every couple of seconds.
  getSession(orgCode, sessionId) {
    return this.http.get(sessionsUrl(orgCode, `/${sessionId}`));
  }

  // Uploads the syllabus (PDF, DOCX, JPG, or PNG; max 100 MB) and triggers
  // analysis server-side (ENG-23217). The file can be replaced by uploading
  // again any time before criteria are submitted — including after a failed
  // analysis, which re-runs it. `onUploadProgress` receives Axios progress
  // events to drive the progress bar.
  uploadSyllabus(orgCode, sessionId, file, { onUploadProgress } = {}) {
    const formData = new FormData();
    formData.append("file", file, file.name);
    return this.http.post(
      sessionsUrl(orgCode, `/${sessionId}/syllabus`),
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        ...(onUploadProgress ? { onUploadProgress } : {}),
      }
    );
  }

  // Explicit escape hatch when syllabus analysis fails or extracts nothing
  // (ENG-23217): creates a new Quick Start session carrying prefill hints
  // (suggestedCriteria) from the upload session. Only an upload session in
  // failed or analyzed status can be converted.
  convertToQuickStart(orgCode, sessionId) {
    return this.http.post(
      sessionsUrl(orgCode, `/${sessionId}/convert-to-quick-start`)
    );
  }

  getStates() {
    return this.http.get("/state-standards/states");
  }

  getProfessions(state) {
    return this.http.get("/state-standards/professions", { params: { state } });
  }

  // Catalog hierarchy: state → subject → sourceName. `getSubjects` returns
  // the top-level subjects for a state (e.g. "Career and Technical Education").
  getSubjects(state) {
    return this.http.get("/state-standards/subjects", { params: { state } });
  }

  // Framework drill-down within a subject (e.g. "Agriculture, Food & Natural
  // Resources" under CTE). Disabled until the caller has both a state and a
  // subject.
  getSourceNames(state, subject) {
    return this.http.get("/state-standards/source-names", {
      params: { state, subject },
    });
  }

  // The body is a discriminated union — the caller picks the mode:
  //   { mode: "profession", state, profession }
  //   { mode: "subject",    state, subject, sourceName? }
  // `sourceName` is optional in subject mode; omit it to include every
  // standard under the chosen subject.
  loadStateStandards(orgCode, sessionId, body) {
    return this.http.post(
      sessionsUrl(orgCode, `/${sessionId}/standards`),
      body
    );
  }

  submitCriteria(orgCode, sessionId, criteria) {
    return this.http.post(
      sessionsUrl(orgCode, `/${sessionId}/criteria`),
      criteria
    );
  }

  getSettings() {
    return this.http.get("/ai-curriculum/settings/catalog");
  }

  getRecommendations(orgCode, sessionId) {
    return this.http.get(sessionsUrl(orgCode, `/${sessionId}/recommendations`));
  }

  // Records the user's thumbs up/down rating on the current recommendation run
  // (ENG-23158). `rating` is "up" | "down" | null (null clears). Latest write
  // wins. Returns the updated recommendations payload with `feedback` applied.
  // Optional/non-blocking — failures are swallowed by the caller.
  updateFeedback(orgCode, sessionId, rating) {
    return this.http.put(sessionsUrl(orgCode, `/${sessionId}/feedback`), {
      rating,
    });
  }

  // Persists the user's keep/remove selection onto the stored recommendations
  // (ENG-23110), which Build My Course honors. Called once per toggle in the
  // review step — the backend emits the per-item funnel events from the flips
  // this call causes (ENG-21163) — and again before buildCourse. Round-trips
  // the GET recommendations shape; the backend merges removedByUser by id and
  // ignores the other fields.
  updateRecommendationSelection(orgCode, sessionId, body) {
    return this.http.patch(
      sessionsUrl(orgCode, `/${sessionId}/recommendations`),
      body
    );
  }

  buildCourse(orgCode, sessionId, { classroomId } = {}) {
    return this.http.post(sessionsUrl(orgCode, `/${sessionId}/course`), {
      classroomId,
    });
  }

  // Downloads a ZIP of the session's accumulated S3 artifacts. Admin-only and
  // feature-flag gated server-side (non-admins / disabled orgs get 403). Always
  // 200 with at least session_metadata.json for a valid session. The saved
  // filename comes from the response Content-Disposition header.
  downloadArtifacts(orgCode, sessionId) {
    return this.downloadBlob(
      {
        url: sessionsUrl(orgCode, `/${sessionId}/artifacts/download`),
        method: "get",
      },
      `ai-curriculum-session-${sessionId}`,
      "zip",
      {
        blobOptions: { type: "application/zip" },
        preferContentDispositionFilename: true,
      }
    );
  }
}
